import {
  DisplaySurveyType,
  SurveyPosition,
  type PostHog,
} from 'posthog-js'

export type HelpSupportAuthor = 'customer' | 'AI' | 'human'
export type HelpSupportStatus = 'new' | 'open' | 'pending' | 'on_hold' | 'resolved'

export interface HelpSupportMessage {
  id: string
  content: string
  authorType: HelpSupportAuthor
  authorName?: string
  createdAt: string
}

export interface HelpSupportTicket {
  id: string
  status: HelpSupportStatus
  lastMessage?: string
  lastMessageAt?: string
  messageCount: number
  createdAt: string
  unreadCount: number
}

export interface HelpSupportOverview {
  tickets: HelpSupportTicket[]
  currentTicketId: string | null
}

export interface HelpSupportThread {
  ticketId: string
  status: HelpSupportStatus
  messages: HelpSupportMessage[]
  hasMore: boolean
  unreadCount: number
}

export interface HelpSupportSendResult {
  ticketId: string
  status: HelpSupportStatus
}

export interface HelpCenter {
  openSupport(): Promise<HelpSupportOverview | null>
  getSupportMessages(ticketId: string): Promise<HelpSupportThread | null>
  sendSupportMessage(
    message: string,
    ticketId?: string | null,
    newTicket?: boolean,
  ): Promise<HelpSupportSendResult | null>
  markSupportRead(ticketId: string): Promise<void>
  openFeedback(language?: string): void
  openDocumentation(): void
}

interface HelpCenterProvider extends HelpCenter {
  setIdentity(distinctId: string, identityHash: string): void
  clearIdentity(): void
}

const noopProvider: HelpCenterProvider = {
  openSupport: async () => null,
  getSupportMessages: async () => null,
  sendSupportMessage: async () => null,
  markSupportRead: async () => undefined,
  openFeedback: () => undefined,
  openDocumentation: () => undefined,
  setIdentity: () => undefined,
  clearIdentity: () => undefined,
}

let provider: HelpCenterProvider = noopProvider

export const helpCenter: HelpCenter = {
  openSupport: () => provider.openSupport(),
  getSupportMessages: ticketId => provider.getSupportMessages(ticketId),
  sendSupportMessage: (message, ticketId, newTicket) =>
    provider.sendSupportMessage(message, ticketId, newTicket),
  markSupportRead: ticketId => provider.markSupportRead(ticketId),
  openFeedback: language => provider.openFeedback(language),
  openDocumentation: () => provider.openDocumentation(),
}

export const setHelpCenterProvider = (next?: HelpCenterProvider | null) => {
  provider = next ?? noopProvider
}

export const setHelpCenterIdentity = (distinctId: string, identityHash: string) => {
  provider.setIdentity(distinctId, identityHash)
}

export const clearHelpCenterIdentity = () => {
  provider.clearIdentity()
}

const normalizeSurveyLanguage = (language?: string): string | undefined => {
  const value = language?.trim().replace('_', '-')
  return value || undefined
}

export const createPostHogHelpCenterProvider = (
  posthog: PostHog,
  feedbackSurveyId?: string,
): HelpCenterProvider => ({
  async openSupport() {
    try {
      if (!posthog.conversations.isAvailable()) return null

      const response = await posthog.conversations.getTickets({ limit: 20, offset: 0 })
      if (!response) return null

      const tickets: HelpSupportTicket[] = response.results.map(ticket => ({
        id: ticket.id,
        status: ticket.status,
        lastMessage: ticket.last_message,
        lastMessageAt: ticket.last_message_at,
        messageCount: ticket.message_count,
        createdAt: ticket.created_at,
        unreadCount: ticket.unread_count ?? 0,
      }))
      const currentTicketId = posthog.conversations.getCurrentTicketId()
        ?? tickets.find(ticket => ticket.status !== 'resolved')?.id
        ?? tickets[0]?.id
        ?? null

      return { tickets, currentTicketId }
    }
    catch {
      return null
    }
  },
  async getSupportMessages(ticketId) {
    try {
      if (!posthog.conversations.isAvailable()) return null

      const response = await posthog.conversations.getMessages(ticketId)
      if (!response) return null

      return {
        ticketId: response.ticket_id,
        status: response.ticket_status,
        messages: response.messages
          .filter(message => !message.is_private)
          .map(message => ({
            id: message.id,
            content: message.content,
            authorType: message.author_type,
            authorName: message.author_name,
            createdAt: message.created_at,
          })),
        hasMore: response.has_more,
        unreadCount: response.unread_count,
      }
    }
    catch {
      return null
    }
  },
  async sendSupportMessage(message, ticketId, newTicket = false) {
    try {
      if (!posthog.conversations.isAvailable()) return null

      if (ticketId && !newTicket) {
        await posthog.conversations.getMessages(ticketId)
      }
      const response = await posthog.conversations.sendMessage(
        message,
        undefined,
        newTicket,
      )
      return response
        ? { ticketId: response.ticket_id, status: response.ticket_status }
        : null
    }
    catch {
      return null
    }
  },
  async markSupportRead(ticketId) {
    try {
      if (posthog.conversations.isAvailable()) {
        await posthog.conversations.markAsRead(ticketId)
      }
    }
    catch {
      // Read state must never break the Help Center.
    }
  },
  openFeedback(language) {
    if (!feedbackSurveyId) return

    try {
      const overrideDisplayLanguage = normalizeSurveyLanguage(language)
      if (overrideDisplayLanguage) {
        posthog.set_config({
          override_display_language: overrideDisplayLanguage,
        })
      }
      posthog.displaySurvey(feedbackSurveyId, {
        displayType: DisplaySurveyType.Popover,
        position: SurveyPosition.Center,
        ignoreConditions: true,
        ignoreDelay: true,
      })
    }
    catch {
      // Help must never break the application.
    }
  },
  openDocumentation() {
    // Reserved for the future documentation surface.
  },
  setIdentity(distinctId, identityHash) {
    try {
      posthog.setIdentity(distinctId, identityHash)
    }
    catch {
      // Support identity verification is optional at runtime.
    }
  },
  clearIdentity() {
    try {
      posthog.clearIdentity()
    }
    catch {
      // The analytics reset path still clears the PostHog browser state.
    }
  },
})
